import React, { createContext, useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const SiteContext = createContext();

export const useSite = () => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error('useSite must be used within SiteProvider');
  }
  return context;
};

export const SiteProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [config, setConfig] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [fontSize, setFontSize] = useState(16);
  const [highContrast, setHighContrast] = useState(false);
  const [loading, setLoading] = useState(true);
  const [moduleAccess, setModuleAccess] = useState({ trade_license: false });

  useEffect(() => {
    loadSiteConfig();
    loadLanguages();
    loadModuleAccess();

    // Load preferences from localStorage
    const savedLang = localStorage.getItem('language') || 'en';
    const savedFontSize = parseInt(localStorage.getItem('fontSize')) || 16;
    const savedContrast = localStorage.getItem('highContrast') === 'true';

    setCurrentLanguage(savedLang);
    setFontSize(savedFontSize);
    setHighContrast(savedContrast);
    i18n.changeLanguage(savedLang);

    document.documentElement.style.fontSize = `${savedFontSize}px`;
    if (savedContrast) {
      document.body.classList.add('high-contrast');
    }
  }, [i18n]);

  const loadSiteConfig = async () => {
    try {
      const response = await api.get('/config/active/');
      setConfig(response.data);
      // Apply header bg color from config
      if (response.data?.header_bg_color) {
        document.documentElement.style.setProperty(
          '--nav-bg-color',
          response.data.header_bg_color
        );
      }
    } catch (error) {
      console.error('Error loading site config:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLanguages = async () => {
    try {
      const response = await api.get('/languages/');
      if (Array.isArray(response.data)) {
        setLanguages(response.data);
      } else if (response.data.results && Array.isArray(response.data.results)) {
        setLanguages(response.data.results);
      } else {
        setLanguages([]);
      }
    } catch (error) {
      console.error('Error loading languages:', error);
      setLanguages([]);
    }
  };

  const loadModuleAccess = async () => {
    try {
      const response = await api.get('/module-access/');
      setModuleAccess(response.data);
    } catch (error) {
      console.error('Error loading module access:', error);
      setModuleAccess({ trade_license: false });
    }
  };

  const changeLanguage = (lang) => {
    setCurrentLanguage(lang);
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
    // Trigger Google Translate if available
    triggerGoogleTranslate(lang);
  };

  const triggerGoogleTranslate = (lang) => {
    try {
      const hostname = window.location.hostname;
      // We overwrite using both with and without leading dot to ensure we hit the right origin cookie
      const options = [
        `path=/; domain=${hostname}`,
        `path=/; domain=.${hostname}`,
        `path=/`
      ];

      if (lang === 'en') {
        // Clear cookies to restore english natively
        options.forEach(opt => {
          document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; ${opt}`;
        });
        window.location.reload();
      } else {
        // Set cookies to target language
        const value = `/en/${lang}`;
        const expires = new Date();
        expires.setTime(expires.getTime() + (365 * 24 * 60 * 60 * 1000));

        options.forEach(opt => {
          document.cookie = `googtrans=${value}; expires=${expires.toUTCString()}; ${opt}`;
        });
        window.location.reload();
      }
    } catch (e) {
      console.error('Translate error:', e);
    }
  };

  const changeFontSize = (size) => {
    setFontSize(size);
    document.documentElement.style.fontSize = `${size}px`;
    localStorage.setItem('fontSize', size);
  };

  const toggleHighContrast = () => {
    const newValue = !highContrast;
    setHighContrast(newValue);
    if (newValue) {
      document.body.classList.add('high-contrast');
    } else {
      document.body.classList.remove('high-contrast');
    }
    localStorage.setItem('highContrast', newValue);
  };

  const value = {
    config,
    languages,
    currentLanguage,
    changeLanguage,
    fontSize,
    changeFontSize,
    highContrast,
    toggleHighContrast,
    loading,
    moduleAccess
  };

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
};
